
import { useAuth } from "@/context/AuthContext";
import { BirdieRecord, TeeBox, User, getManualPlayerId } from "@/types";
import { toast } from "@/hooks/use-toast";
import {
  createScore,
  updateScore,
  deleteScore
} from "@/integrations/supabase/scores";

interface UseBirdieActionsProps {
  courseId: string;
  userId: string;
  fetchBirdieRecords: () => Promise<void>;
  setIsLoading: (isLoading: boolean) => void;
  resetForm: () => void;
  setEditingRecordId: (id: string | null) => void;
  setIsEditing: (isEditing: boolean) => void;
}

export function useBirdieActions({
  courseId,
  userId,
  fetchBirdieRecords,
  setIsLoading,
  resetForm,
  setEditingRecordId,
  setIsEditing
}: UseBirdieActionsProps) {
  const { user } = useAuth();

  const handleSubmit = async (
    holeNumber: number,
    teeBox: TeeBox,
    date: string,
    notes: string,
    relevantGameIds: string[], // Keep for backward compatibility
    isEditing: boolean,
    editingRecordId: string | null,
    selectedUserId: string,
    gamePlayers?: Record<string, User[]>,
    presentPlayers?: User[],
    scoreType: BirdieRecord['scoreType'] = "birdie"
  ) => {
    // Require at least one present player
    if (!presentPlayers || presentPlayers.length === 0) {
      toast({
        title: "No Players Selected",
        description: "Please select at least one player who was present for this birdie.",
        variant: "destructive",
      });
      return false;
    }
    
    setIsLoading(true);
    
    try {
      const effUserId = selectedUserId === "current" ? (user?.id || userId) : selectedUserId;
      
      // Get list of players present
      const presentPlayerIds = presentPlayers.map(player => player.id);
      
      // Ensure the current user is included
      if (user && !presentPlayerIds.includes(user.id)) {
        presentPlayerIds.push(user.id);
      }
      
      // Check if scoring for a manual player
      const manualPlayerId = getManualPlayerId(effUserId);

      if (isEditing && editingRecordId) {
        // Update existing score
        const { error } = await updateScore(editingRecordId, {
          tee: teeBox,
          score_type: scoreType,
          score_date: new Date(date).toISOString(),
          notes,
          presentPlayerIds: presentPlayerIds
        });

        if (error) throw new Error(error);

        const typeLabel = scoreType === "double_eagle" ? "albatross" : scoreType;
        toast({
          title: "Score Updated!",
          description: `Your ${typeLabel} for hole ${holeNumber} has been updated.`
        });
      } else {
        // Create new score
        const { error } = await createScore({
          user_id: manualPlayerId ? (user?.id || userId) : effUserId,
          course_id: courseId,
          hole_number: holeNumber,
          tee: teeBox,
          score_type: scoreType,
          score_date: new Date(date).toISOString(),
          notes,
          presentPlayerIds: presentPlayerIds,
          presentPlayers: presentPlayers,
          manualPlayerId: manualPlayerId || undefined,
        });
        
        if (error) throw new Error(error);
        
        const typeLabel = scoreType === "double_eagle" ? "albatross" : scoreType;
        toast({
          title: "Score Recorded!",
          description: `You've recorded a ${typeLabel} on hole ${holeNumber} from the ${teeBox} tees.`
        });
      }
      
      // Refresh the birdie records
      await fetchBirdieRecords();
      
      if (!isEditing) {
        resetForm();
      } else {
        setEditingRecordId(null);
        setIsEditing(false);
      }
      
      return true;
    } catch (error) {
      console.error("Error saving birdie record:", error);
      toast({
        title: "Error",
        description: `Failed to ${isEditing ? 'update' : 'save'} birdie record.`,
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (record: BirdieRecord) => {
    setEditingRecordId(record.id);
    setIsEditing(true);
    return record;
  };

  const handleDelete = async (record: BirdieRecord) => {
    setIsLoading(true);
    
    try {
      const { error } = await deleteScore(record.id);
      
      if (error) throw new Error(error);
      
      toast({
        title: "Birdie Deleted",
        description: `Birdie for hole ${record.holeNumber} on ${new Date(record.date).toLocaleDateString()} has been deleted.`,
      });
      
      // Refresh the birdie records
      await fetchBirdieRecords();
    } catch (error) {
      console.error("Error deleting birdie record:", error);
      toast({
        title: "Error",
        description: "Failed to delete birdie record.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = (editingRecordId: string | null) => {
    if (editingRecordId) {
      resetForm();
      setEditingRecordId(null);
      setIsEditing(false);
      return false;
    }
    return true;
  };

  return {
    handleSubmit,
    handleEdit,
    handleDelete,
    handleCancel
  };
}
